import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Building2, 
  Play, 
  Clock, 
  Award,
  User,
  FileText,
  Video,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PublicDDS = () => {
  const { token } = useParams();
  const [dds, setDds] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState(null);
  const [pointsEarned, setPointsEarned] = useState(0);

  useEffect(() => {
    fetchDDS();
  }, [token]);

  const fetchDDS = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/dds/${token}`);
      const data = response.data;
      
      setDds(data.dds);
      setEmployee(data.employee);
      setCompanyName(data.company_name);
      
      if (data.already_confirmed) {
        setAlreadyConfirmed(true);
        setConfirmedAt(data.confirmed_at);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao carregar DDS');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    
    try {
      const response = await axios.post(`${API_URL}/public/dds/${token}/confirm`);
      const data = response.data;
      
      if (data.already_confirmed) {
        setAlreadyConfirmed(true);
        setConfirmedAt(data.confirmed_at);
        toast.info('Sua presença já foi registrada anteriormente');
      } else {
        setConfirmed(true);
        setPointsEarned(data.points_earned || 0);
        setConfirmedAt(data.confirmed_at);
        toast.success(data.message || 'Presença registrada com sucesso!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar visualização');
    } finally {
      setConfirming(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#0d9488] mx-auto mb-4" />
          <p className="text-slate-600">Carregando DDS...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link Inválido</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            Este link pode ter expirado ou já foi utilizado.
          </p>
        </Card>
      </div>
    );
  }

  // Already confirmed state
  if (alreadyConfirmed) {
    return (
      <>
        <Helmet>
          <title>DDS - Presença Confirmada | CERP Brasil</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Presença Já Registrada</h1>
            <p className="text-slate-600 mb-4">
              Olá <strong>{employee?.nome || 'Colaborador'}</strong>!
            </p>
            <p className="text-slate-500 mb-6">
              Sua presença para o DDS "<strong>{dds?.titulo}</strong>" já foi registrada anteriormente.
            </p>
            {confirmedAt && (
              <p className="text-xs text-slate-400">
                Confirmado em: {new Date(confirmedAt).toLocaleString('pt-BR')}
              </p>
            )}
          </Card>
        </div>
      </>
    );
  }

  // Success state (just confirmed)
  if (confirmed) {
    return (
      <>
        <Helmet>
          <title>DDS Concluído! | CERP Brasil</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-[#0d9488] to-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Parabéns! 🎉</h1>
            <p className="text-slate-600 mb-4">
              <strong>{employee?.nome || 'Colaborador'}</strong>, sua presença foi registrada com sucesso!
            </p>
            
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className="h-6 w-6 text-amber-500" />
                <span className="text-2xl font-bold text-amber-600">+{pointsEarned}</span>
              </div>
              <p className="text-amber-700 text-sm">Pontos de Gamificação!</p>
            </div>
            
            <p className="text-slate-500 text-sm">
              Você completou o DDS "<strong>{dds?.titulo}</strong>".
              <br />Continue participando para acumular mais pontos!
            </p>
            
            {companyName && (
              <div className="mt-6 pt-4 border-t flex items-center justify-center gap-2 text-slate-400 text-sm">
                <Building2 className="h-4 w-4" />
                <span>{companyName}</span>
              </div>
            )}
          </Card>
        </div>
      </>
    );
  }

  // Main DDS view
  return (
    <>
      <Helmet>
        <title>{dds?.titulo ? `${dds.titulo} | CERP Brasil` : 'DDS | CERP Brasil'}</title>
        <meta name="description" content="Diálogo Diário de Segurança - Assista e confirme sua presença" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white" data-testid="public-dds-page">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0d9488] text-white py-6">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  {dds?.tipo === 'video' ? (
                    <Video className="h-5 w-5" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-white/70">Diálogo Diário de Segurança</p>
                  <h1 className="text-lg font-semibold">{dds?.titulo}</h1>
                </div>
              </div>
              {companyName && (
                <div className="hidden sm:flex items-center gap-2 text-white/70 text-sm">
                  <Building2 className="h-4 w-4" />
                  <span>{companyName}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="max-w-4xl mx-auto px-4 -mt-4">
          <Card className="p-4 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0d9488]/10 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-[#0d9488]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{employee?.nome || 'Colaborador'}</p>
                  <p className="text-sm text-slate-500">{employee?.cargo || employee?.setor || ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {dds?.duracao_minutos || 5} min
                </Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                  <Award className="h-3 w-3 mr-1" />
                  +{dds?.pontos || 10} pts
                </Badge>
              </div>
            </div>
          </Card>
        </div>

        {/* DDS Content */}
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Card className="overflow-hidden">
            {/* Video Content */}
            {dds?.tipo === 'video' && dds?.video_url && (
              <div className="aspect-video bg-black">
                {dds.video_url.includes('youtube.com') || dds.video_url.includes('youtu.be') ? (
                  <iframe
                    src={dds.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={dds.titulo}
                  />
                ) : dds.video_url.includes('vimeo.com') ? (
                  <iframe
                    src={dds.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={dds.titulo}
                  />
                ) : (
                  <video
                    src={dds.video_url}
                    controls
                    className="w-full h-full"
                    poster={dds.thumbnail_url}
                  >
                    Seu navegador não suporta vídeos HTML5.
                  </video>
                )}
              </div>
            )}

            {/* Text Content */}
            <CardContent className="p-6">
              {/* Description */}
              {dds?.descricao && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-2">Sobre este DDS</h2>
                  <p className="text-slate-600">{dds.descricao}</p>
                </div>
              )}

              {/* Rich Text Content (Markdown) */}
              {dds?.tipo === 'texto' && dds?.conteudo_texto && (
                <div className="prose prose-slate max-w-none" data-color-mode="light">
                  <MDEditor.Markdown source={dds.conteudo_texto} />
                </div>
              )}

              {/* Category Badge */}
              {(dds?.categoria || dds?.categoria_nome) && (
                <div className="mt-6 pt-4 border-t">
                  <Badge variant="secondary">
                    Categoria: {dds.categoria || dds.categoria_nome}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Confirm Button */}
          <Card className="mt-6 p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Confirme sua Presença
              </h3>
              <p className="text-slate-600 mb-6">
                Após assistir/ler o conteúdo, clique no botão abaixo para registrar sua presença e ganhar pontos.
              </p>
              
              <Button 
                onClick={handleConfirm}
                disabled={confirming}
                size="lg"
                className="bg-[#0d9488] hover:bg-[#0b7d73] px-8 py-6 text-lg"
                data-testid="confirm-view-btn"
              >
                {confirming ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Confirmar Visualização
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-400 mt-4">
                Este link é de uso único. Após confirmar, não poderá ser reutilizado.
              </p>
            </div>
          </Card>

          {/* Footer */}
          <div className="text-center py-6 text-sm text-slate-500">
            <p>CERP Brasil - Gestão de Segurança e Saúde no Trabalho</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicDDS;
